import type { AnonObject } from '@vy/utils';
import { isNullOrUndefined } from '@vy/utils';
import type { ChartOptions } from 'chart.js';
import type { ChartDataset, ChartTypeRegistry } from 'chart.js';
import { OldVyPalette } from '../../../../assets/old-themes/old-vy/palette';
import { formatNumberAbbreviated } from '../../../../utils/number';

type CustomChartDataset<TType extends keyof ChartTypeRegistry> = ChartDataset<TType> & {
  metadata: AnonObject & { priceValues: number[] };
};

export function generateUsageLineItemBarChartOptions(
  options: {
    unitCategory?: string;
    currency?: string;
    datasetLabelsLength?: number;
  } = {},
): ChartOptions<'bar' | 'line'> {
  const { unitCategory, currency, datasetLabelsLength } = options;
  const barThickness = calculateBarWidth(datasetLabelsLength ?? 30);
  const title = unitCategory ?? 'Units';
  const capitalizedTitle = title.charAt(0).toUpperCase() + title.slice(1);

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        border: {
          dash: [2, 5],
          display: false,
        },
        ticks: {
          font: {
            size: 14,
            family: 'Inter, sans-serif',
          },
          color: '#9F9F9F',
          callback(val) {
            const formattedVal = formatNumberAbbreviated(Number(val));
            const emptySpaceToAdd =
              formattedVal.length < MAX_LABEL_LENGTH
                ? ' '.repeat(MAX_SPACES_TO_ADD - formattedVal.length)
                : ' ';

            return `${emptySpaceToAdd}${formattedVal}`;
          },
        },
      },
      x: {
        border: {
          display: true,
          color: '#C2C2C2',
        },
        ticks: {
          font: {
            size: 10,
            family: 'Inter, sans-serif',
          },
          color: '#9F9F9F',
        },
        grid: {
          display: false,
        },
      },
    },
    datasets: {
      bar: {
        barThickness,
        borderRadius: 6,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: capitalizedTitle,
        align: 'start',
        color: '#1b3d5599',
        font: {
          size: 12,
          family: 'Inter, sans-serif',
          weight: 'normal',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        callbacks: {
          label(context) {
            const numberFormatter = new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });

            const customDataset = context.dataset as CustomChartDataset<'bar' | 'line'>;
            const datasetMetadata = customDataset.metadata;
            if (isNullOrUndefined(datasetMetadata)) {
              return numberFormatter.format(Number(context.formattedValue));
            }

            const totalUnits = Number(context.raw);
            const totalPrice = datasetMetadata.priceValues[context.dataIndex];

            const displayUnits = new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(totalUnits);
            const displayPrice = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency ?? 'USD',
            }).format(totalPrice);

            return `Units: ${displayUnits}\nPrice: ${displayPrice}`;
          },
        },
      },
    },
  };
}

export const barColors = [OldVyPalette.primary.main, OldVyPalette.secondary.main, OldVyPalette.tertiary.main];

const MAX_LABEL_LENGTH = 4;
const MAX_SPACES_TO_ADD = 8;
const DISPLAY_WIDTH_PERCENTAGE = 0.5;
const GAP_WIDTH = 15;

const calculateBarWidth = (datasetLabelsLength: number) => {
  const viewportWidth = window.innerWidth;
  const displayWidth = DISPLAY_WIDTH_PERCENTAGE * viewportWidth;
  if (datasetLabelsLength <= 1) return displayWidth;

  const totalGapSpace = (datasetLabelsLength - 1) * GAP_WIDTH;
  const remainingWidth = displayWidth - totalGapSpace;

  return Math.max(remainingWidth / datasetLabelsLength, 8);
};
